import React, {Component} from 'react';
import _ from 'lodash';
import FileDropzone from './FileDropzone';
import {Col, Row} from 'react-bootstrap';
import {FormattedMessage} from 'react-intl';
import axios from 'axios';

class SubmissionFileUpload extends Component {

    constructor(props) {
        super(props);

        this.state = {
            files: {},
            initializing: true
        }
        this.uploadUrl = process.env.REACT_APP_API_TEMP_UPLOAD_URL;
    }

    /**
     * @param files
     */
    initPreviousFiles = (files) => {
        // die IDs der state-files sind im Endeffekt Indizes
        let idCounter = 0;

        if (files) {
            const previousAssets = _.map(files, (asset) => {
                return {
                    id: (idCounter++).toString(),
                    status: 'loaded',
                    progressPercent: 100,
                    file: {
                        name: asset.name,
                    },
                    temporaryFileData: {
                        id: asset.id
                    }
                }
            });

            this.setState({
                files: previousAssets
            });

            // formik value setzen, da hier sonst ein alter Wert steht und die API versucht eine nicht mehr
            // existente temp-Datei zu finden
            this.props.onFilesChanged(_.map(
                _.filter(previousAssets, file => ['completed', 'loaded'].includes(file.status)),
                file => ({temporaryId: file.temporaryFileData.id})
            ));
        }
    }

    /**
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.submission && this.state.initializing) {
            let previousFiles;
            if (prevProps.widget.type === 'logo_upload') {
                previousFiles = prevProps.submission.logoImage ? [prevProps.submission.logoImage] : [];
            } else {
                // Zuvor hochgeladene Dateien wurden von TemporaryFiles in Assets verwandelt, wodurch diese nicht im
                // value des jeweiligen FormDatums liegen sondern unter assets
                previousFiles = _.get(
                    _.find(prevProps.submission.formData, (datum) => datum.id === prevProps.widget.id),
                    'assets',
                    []
                );
            }

            this.initPreviousFiles(previousFiles);
            this.setState({initializing: false});
        }
    }

    /**
     *
     * @param file
     */
    removeFile = file => {
        this.setState({initializing: false});
        this.setState(prevState => {
            const newState = _.cloneDeep(prevState);
            newState.files = _.filter(newState.files, (stateFile) => {
                return stateFile.id.toString() !== file.id.toString();
            });
            return newState;
        }, this.notifyCompletedFiles);
    };

    /**
     *
     * @param files
     */
    onFilesAdded = files => {
        this.setState({initializing: false});
        const items = _.reduce(files, (acc, file) => {
            // lodash.uniqueId() hat immer einen Wert von 1 erzeugt, dadurch wurden Dateien entfernt...
            const id = (new Date()).getTime();
            acc[id] = {id, file, progressPercent: 0, status: 'pending'};
            return acc;
        }, {});

        this.setState(prevState => ({
            files: _.merge({}, prevState.files, items)
        }), () => {
            // Alle neu hochgeladenen Dateien uploaden
            _.each(_.filter(this.state.files, file => file.status === 'pending'), this.upload);
        });
    };

    /**
     * Gibt dem User Bescheid, wenn eine Datei hochgeladen wurde und setzt "temporaryId" mittels der prop-Methode
     * "onFilesChanges" den Wert als Formik fieldValue, welcher innerhalb der API verwendet wird um die Datei als Asset
     * an der Einreichung zu referenzieren.
     */
    notifyCompletedFiles = () => {
        if (this.props.onFilesChanged) {
            this.props.onFilesChanged(_.map(
                _.filter(this.state.files, file => ['completed', 'loaded'].includes(file.status)),
                file => ({temporaryId: file.temporaryFileData.id})
            ));
        }
    };

    /**
     *
     * @param file
     * @param callback
     */
    updateFileState = (file, callback) => {
        this.setState(prevState => {
            if (this.props.widget.type === 'logo_upload') {
                // Es darf immer nur ein Logo geben, wodurch das alte Bild entfällt und durch das neue ersetzt wird
                return {files: {0: file}}
            } else {
                const newState = {files: {...prevState.files}};
                newState.files[file.id] = file;
                return newState;
            }
        }, () => {
            this.notifyCompletedFiles();
            if (callback) {
                callback();
            }
        });
    };

    /**
     * Prüft, ob die Bilddimensionen eine Auflösung von 200x200 Pixel überschreitet
     *
     * @param file
     * @return {Promise<boolean>}
     */
    validateImageDimensions = async (file) => {
        if (this.props.widget.type === 'logo_upload') {
            const validImage = await createImageBitmap(file.file).then((bmp) => {
                return !(bmp.width > 200 || bmp.height > 200);
            });
            if (!validImage) {
                file.status = 'error_dimensions';
                this.setState(prevState => {
                    const files = prevState.files
                    return {files: {...files}};
                });
                return false;
            }
        }

        return true;
    };

    /**
     *
     * @param file
     */
    upload = async (file) => {
        if (!await this.validateImageDimensions(file)) {
            return;
        }

        file.status = 'uploading';
        this.updateFileState(file, () => {
            const formData = new FormData();
            formData.append('file', file.file, file.file.name);

            axios.post(this.uploadUrl,
                formData, {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: event => {
                        if (event.lengthComputable) {
                            file.progressPercent = (event.loaded / event.total) * 100;
                            this.updateFileState(file);
                        }
                    }
                }
            ).then((result) => {
                file.status = 'completed';
                file.temporaryFileData = result.data;
            }).catch(() => {
                file.status = 'error';
            }).finally(() => {
                file.progressPercent = 100;
                this.updateFileState(file);
            });
        });
    };

    render() {

        const progressBarClass = status => {
            let cssClass = 'progress-bar ';
            if (status === 'completed' || status === 'loaded') cssClass += 'bg-success';
            if (status === 'error') cssClass += 'bg-danger';
            return cssClass;
        };

        return (
            <div className="file-upload">
                <Row>
                    {
                        !this.props.disabled &&
                        <Col sm={2}>
                            <FileDropzone onFilesAdded={this.onFilesAdded}
                                          allowedFileTypes={this.props.allowedFileTypes}/>
                        </Col>
                    }
                    <Col>
                        {_.values(this.state.files).map(file => (
                            <Row key={file.id}>
                                <Col>
                                    <div className="progress">
                                        <div className={progressBarClass(file.status)} role="progressbar"
                                             style={{width: `${file.progressPercent}%`}}>{file.progressPercent}%
                                        </div>
                                    </div>
                                    <div>
                                        {
                                            (file.temporaryFileData && file.status === 'loaded') ?
                                                <a href={`${process.env.REACT_APP_API_ASSET_BASE_URL}/${file.temporaryFileData.id}`}>
                                                    {file.file.name} <i className="fa fa-download" aria-hidden="true"/>
                                                </a> :
                                                file.file.name
                                        }
                                        {
                                            (file.status === 'error' || file.status === 'error_dimensions') &&
                                            <span className="text-danger">
                                                - <FormattedMessage id={`upload.${file.status}`}/>
                                            </span>
                                        }
                                    </div>
                                </Col>
                                {
                                    !this.props.disabled &&
                                    <Col sm={2}>
                                        <button type="button" className="btn btn-secondary btn-sm"
                                                onClick={() => this.removeFile(file)}>
                                            <i className="fa fa-trash-o" aria-hidden="true"/>
                                        </button>
                                    </Col>
                                }
                            </Row>
                        ))}
                    </Col>
                </Row>
            </div>
        );
    }
}


export default SubmissionFileUpload;
